import { Component, Show, For, createSignal, createMemo, createEffect, onCleanup } from "solid-js"
import { ArrowRightSquare, ArrowUpLeft, File as FileIcon, Folder as FolderIcon, FolderPlus, Loader2, X } from "lucide-solid"
import type { FileSystemEntry, FileSystemListingMetadata } from "../../../server/src/api-types"
import { WINDOWS_DRIVES_ROOT } from "../../../server/src/api-types"
import { serverApi } from "../lib/api-client"
import { showAlertDialog, showPromptDialog } from "../stores/alerts"
import { useI18n } from "../lib/i18n"
import DirectoryBrowserAddress, { type DirectoryDestination } from "./directory-browser-address"

function normalizePathKey(input?: string | null) {
  if (!input || input === "." || input === "./") {
    return "."
  }
  if (input === WINDOWS_DRIVES_ROOT) {
    return WINDOWS_DRIVES_ROOT
  }
  let normalized = input.replace(/\\/g, "/")
  if (/^[a-zA-Z]:/.test(normalized)) {
    const [drive, rest = ""] = normalized.split(":")
    const suffix = rest.startsWith("/") ? rest : rest ? `/${rest}` : "/"
    return `${drive.toUpperCase()}:${suffix.replace(/\/+/g, "/")}`
  }
  if (normalized.startsWith("//")) {
    return `//${normalized.slice(2).replace(/\/+/g, "/")}`
  }
  if (normalized.startsWith("/")) {
    return `/${normalized.slice(1).replace(/\/+/g, "/")}`
  }
  normalized = normalized.replace(/^\.\/+/, "").replace(/\/+/g, "/")
  return normalized === "" ? "." : normalized
}


function isAbsolutePathLike(input: string) {
  return input.startsWith("/") || /^[a-zA-Z]:/.test(input) || input.startsWith("\\\\")
}

interface DirectoryBrowserDialogProps {
  open: boolean
  mode?: "directories" | "files"
  title: string
  description?: string
  initialPath?: string
  onSelect: (absolutePath: string, entry?: FileSystemEntry) => void
  onClose: () => void
}

function resolveAbsolutePath(root: string, relativePath: string) {
  if (!root) {
    return relativePath
  }
  if (!relativePath || relativePath === "." || relativePath === "./") {
    return root
  }
  if (isAbsolutePathLike(relativePath)) {
    return relativePath
  }
  const separator = root.includes("\\") ? "\\" : "/"
  const trimmedRoot = root.endsWith(separator) ? root : `${root}${separator}`
  const normalized = relativePath.replace(/[\\/]+/g, separator).replace(/^[\\/]+/, "")
  return `${trimmedRoot}${normalized}`
}

function getAbsolutePathFromMetadata(metadata: FileSystemListingMetadata | null) {
  if (!metadata || metadata.pathKind === "drives") {
    return ""
  }
  if (metadata.pathKind === "relative") {
    return resolveAbsolutePath(metadata.rootPath, metadata.currentPath)
  }
  return metadata.displayPath
}

const DirectoryBrowserDialog: Component<DirectoryBrowserDialogProps> = (props) => {
  const { t } = useI18n()
  const [rootPath, setRootPath] = createSignal("")
  const [loading, setLoading] = createSignal(false)
  const [error, setError] = createSignal<string | null>(null)
  const [pathInput, setPathInput] = createSignal("")
  const [pathInputDirty, setPathInputDirty] = createSignal(false)
  const [creatingFolder, setCreatingFolder] = createSignal(false)
  const [directoryChildren, setDirectoryChildren] = createSignal<Map<string, FileSystemEntry[]>>(new Map())
  const [loadingPaths, setLoadingPaths] = createSignal<Set<string>>(new Set())
  const [currentPathKey, setCurrentPathKey] = createSignal<string | null>(null)
  const [currentMetadata, setCurrentMetadata] = createSignal<FileSystemListingMetadata | null>(null)
  const [initialAbsolutePath, setInitialAbsolutePath] = createSignal("")
  const [addressOpen, setAddressOpen] = createSignal(false)

  const metadataCache = new Map<string, FileSystemListingMetadata>()
  const inFlightRequests = new Map<string, Promise<FileSystemListingMetadata>>()
  let latestNavigationId = 0
  let openingGeneration = 0

  function resetState() {
    ++openingGeneration
    ++latestNavigationId
    setRootPath("")
    setDirectoryChildren(new Map<string, FileSystemEntry[]>())
    setLoadingPaths(new Set<string>())
    setCurrentPathKey(null)
    setCurrentMetadata(null)
    setPathInput("")
    setPathInputDirty(false)
    setInitialAbsolutePath("")
    setAddressOpen(false)
    metadataCache.clear()
    inFlightRequests.clear()
    setError(null)
  }

  createEffect(() => {
    if (!props.open) {
      return
    }
    resetState()
    void initialize(openingGeneration)

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault()
        props.onClose()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    onCleanup(() => {
      window.removeEventListener("keydown", handleKeyDown)
    })
  })

  async function initialize(generation: number) {
    setLoading(true)
    try {
      const startPath = props.initialPath?.trim()
      if (startPath) {
        const metadata = await navigateTo(startPath)
        if (generation !== openingGeneration || !props.open) return
        if (metadata) {
          // Capture the canonical absolute path actually opened so the Initial Path
          // shortcut targets exactly where the dialog started (the server resolves
          // relative paths against homePath in unrestricted mode, not rootPath).
          setInitialAbsolutePath(getAbsolutePathFromMetadata(metadata))
          return
        }
        // initialPath was rejected (e.g. no longer under an allowed root);
        // silently fall back to the default root so the dialog stays usable.
        // Do NOT set initialAbsolutePath, so no broken Initial Path shortcut remains.
        setError(null)
      }
      if (generation === openingGeneration && props.open) await navigateTo(undefined)
    } finally {
      if (generation === openingGeneration && props.open) setLoading(false)
    }
  }

  function applyMetadata(metadata: FileSystemListingMetadata) {
    const key = normalizePathKey(metadata.currentPath)
    setCurrentPathKey(key)
    setCurrentMetadata(metadata)
    setRootPath(metadata.rootPath)
  }

  async function loadDirectory(targetPath?: string): Promise<FileSystemListingMetadata> {
    const generation = openingGeneration
    const key = targetPath ? normalizePathKey(targetPath) : undefined
    if (key) {
      const cached = metadataCache.get(key)
      if (cached) {
        return cached
      }
      const pending = inFlightRequests.get(key)
      if (pending) {
        return pending
      }
    }

    const request = (async () => {
      if (key) {
        setLoadingPaths((prev) => {
          const next = new Set(prev)
          next.add(key)
          return next
        })
      }

      const response = await serverApi.listFileSystem(targetPath, { includeFiles: props.mode === "files" })
      if (generation !== openingGeneration || !props.open) return response.metadata
      const canonicalKey = normalizePathKey(response.metadata.currentPath)
      const entries = response.entries
        .filter((entry) => props.mode === "files" || entry.type === "directory")
        .sort((a, b) => {
          const aDirectory = a.type === "directory" ? 0 : 1
          const bDirectory = b.type === "directory" ? 0 : 1
          if (aDirectory !== bDirectory) return aDirectory - bDirectory
          return a.name.localeCompare(b.name)
        })

      setDirectoryChildren((prev) => {
        const next = new Map(prev)
        next.set(canonicalKey, entries)
        return next
      })

      metadataCache.set(canonicalKey, response.metadata)

      setLoadingPaths((prev) => {
        const next = new Set(prev)
        if (key) {
          next.delete(key)
        }
        next.delete(canonicalKey)
        return next
      })

      return response.metadata
    })()
      .catch((err) => {
        if (key && generation === openingGeneration) {
          setLoadingPaths((prev) => {
            const next = new Set(prev)
            next.delete(key)
            return next
          })
        }
        throw err
      })
      .finally(() => {
        if (key && inFlightRequests.get(key) === request) {
          inFlightRequests.delete(key)
        }
      })

    if (key) {
      inFlightRequests.set(key, request)
    }

    return request
  }

  async function navigateTo(path?: string) {
    const navigationId = ++latestNavigationId
    setError(null)
    try {
      const metadata = await loadDirectory(path)
      if (navigationId !== latestNavigationId || !props.open) {
        return null
      }
      applyMetadata(metadata)
      return metadata
    } catch (err) {
      if (navigationId !== latestNavigationId || !props.open) {
        return null
      }
      const message = err instanceof Error ? err.message : t("directoryBrowser.load.errorFallback")
      setError(message)
      return null
    }
  }

  const folderRows = createMemo<FileSystemEntry[]>(() => {
    const key = currentPathKey()
    return key ? directoryChildren().get(key) ?? [] : []
  })

  function handleNavigateTo(path: string) {
    setAddressOpen(false)
    setPathInputDirty(false)
    void navigateTo(path)
  }

  const currentAbsolutePath = createMemo(() => {
    return getAbsolutePathFromMetadata(currentMetadata())
  })

  createEffect(() => {
    const absolutePath = currentAbsolutePath()
    if (!pathInputDirty()) {
      setPathInput(absolutePath)
    }
  })

  const canSelectCurrent = createMemo(() => Boolean(currentAbsolutePath()))
  const canSubmitPath = createMemo(() => pathInput().trim().length > 0)
  // A trailing separator (but not a root such as "/" or "C:/") names the same folder.
  const folderKey = (path: string) => normalizePathKey(path).replace(/(?<=[^:/])\/+$/, "")
  const canGoToTypedPath = createMemo(() =>
    pathInputDirty() && canSubmitPath() && folderKey(pathInput().trim()) !== folderKey(currentAbsolutePath()))

  const destinations = createMemo<DirectoryDestination[]>(() => {
    const meta = currentMetadata()
    if (!meta) return []
    const current = currentAbsolutePath()
    const result: DirectoryDestination[] = []
    const seen = new Set([normalizePathKey(current)])
    const add = (destination: DirectoryDestination) => {
      const key = normalizePathKey(destination.target)
      if (seen.has(key)) return
      seen.add(key)
      result.push(destination)
    }
    const initial = initialAbsolutePath()
    if (initial) {
      const name = initial.split(/[\\/]/).filter(Boolean).at(-1) ?? initial
      add({ target: initial, label: t("directoryBrowser.goToInitial", { name }), kind: "initial" })
    }
    if (meta.rootPath) {
      add({ target: meta.rootPath, label: t("directoryBrowser.goToRoot"), kind: "root" })
    }
    if (meta.scope === "unrestricted" && meta.homePath) {
      add({ target: meta.homePath, label: t("directoryBrowser.goToHome"), kind: "home" })
    }
    // Use the already loaded listing; typing never starts a speculative filesystem request.
    const input = pathInput().trim().replace(/\\/g, "/")
    const normalizedCurrent = current.replace(/\\/g, "/")
    const base = normalizedCurrent.replace(/\/$/, "")
    const filter = input === normalizedCurrent ? ""
      : input.startsWith(`${base}/`) ? input.slice(base.length + 1)
      : !input.includes("/") && !input.includes(":") ? input : null
    if (!filter || filter.includes("/")) return result
    for (const entry of directoryChildren().get(currentPathKey() ?? "") ?? []) {
      if (entry.type !== "directory" || !entry.name.toLocaleLowerCase().startsWith(filter.toLocaleLowerCase())) continue
      add({ target: entry.absolutePath ?? (isAbsolutePathLike(entry.path) ? entry.path : resolveAbsolutePath(meta.rootPath, entry.path)), label: entry.name, kind: "folder" })
    }
    return result
  })

  function chooseDestination(destination: DirectoryDestination) {
    setAddressOpen(false)
    setPathInputDirty(false)
    void navigateTo(destination.target)
  }

  async function handlePathSubmit() {
    setAddressOpen(false)
    const target = pathInput().trim()
    if (!target) {
      return
    }
    const metadata = await navigateTo(target)
    if (!metadata) {
      return
    }
    setPathInputDirty(false)
    setPathInput(getAbsolutePathFromMetadata(metadata))
  }

  async function handleOpenCurrent() {
    const target = pathInput().trim()
    const metadata = target && target !== currentAbsolutePath() ? await navigateTo(target) : currentMetadata()
    if (!metadata) {
      return
    }
    setPathInputDirty(false)
    const absolute = getAbsolutePathFromMetadata(metadata)
    if (absolute && props.mode !== "files") {
      setPathInput(absolute)
      props.onSelect(absolute)
    }
  }

  function handleEntrySelect(entry: FileSystemEntry) {
    const absolutePath = entry.absolutePath
      ? entry.absolutePath
      : isAbsolutePathLike(entry.path)
        ? entry.path
        : resolveAbsolutePath(rootPath(), entry.path)
    props.onSelect(absolutePath, entry)
  }

  async function handleCreateFolder() {
    if (creatingFolder()) return
    const target = pathInput().trim()
    const metadata = target && target !== currentAbsolutePath() ? await navigateTo(target) : currentMetadata()
    if (!metadata || metadata.pathKind === "drives") {
      return
    }
    setPathInputDirty(false)
    setPathInput(getAbsolutePathFromMetadata(metadata))

    const name =
      (await showPromptDialog(t("directoryBrowser.createFolder.promptMessage"), {
        title: t("directoryBrowser.createFolder.title"),
        inputLabel: t("directoryBrowser.createFolder.inputLabel"),
        inputPlaceholder: t("directoryBrowser.createFolder.inputPlaceholder"),
        confirmLabel: t("directoryBrowser.createFolder.confirmLabel"),
        cancelLabel: t("directoryBrowser.createFolder.cancelLabel"),
      }))?.trim() ?? ""
    if (!name) return

    if (name === "." || name === ".." || name.startsWith("~") || name.includes("/") || name.includes("\\")) {
      showAlertDialog(t("directoryBrowser.createFolder.invalidNameMessage"), {
        variant: "warning",
        detail: t("directoryBrowser.createFolder.invalidNameDetail"),
      })
      return
    }

    setCreatingFolder(true)
    try {
      const parentKey = normalizePathKey(metadata.currentPath)
      metadataCache.delete(parentKey)
      inFlightRequests.delete(parentKey)
      setDirectoryChildren((prev) => {
        const next = new Map(prev)
        next.delete(parentKey)
        return next
      })

      const created = await serverApi.createFileSystemFolder(metadata.currentPath, name)
      await navigateTo(created.path)
    } catch (err) {
      const message = err instanceof Error ? err.message : t("directoryBrowser.createFolder.errorFallback")
      showAlertDialog(message, { variant: "error", title: t("directoryBrowser.createFolder.errorFallback") })
    } finally {
      setCreatingFolder(false)
    }
  }

  function isPathLoading(path: string) {
    return loadingPaths().has(normalizePathKey(path))
  }

  function handleOverlayClick(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      props.onClose()
    }
  }

  return (
    <Show when={props.open}>
      <div class="fixed inset-0 z-[1305] flex items-center justify-center bg-black/60 p-6" onClick={handleOverlayClick}>
        <div class="modal-surface directory-browser-modal" role="dialog" aria-modal="true">
          <div class="panel directory-browser-panel">
            <div class="directory-browser-header">
              <div class="directory-browser-heading">
                <h3 class="directory-browser-title">{props.title}</h3>
                <p class="directory-browser-description">
                  {props.description || t("directoryBrowser.defaultDescription")}
                </p>
              </div>
              <button type="button" class="directory-browser-close" aria-label={t("directoryBrowser.close")} onClick={props.onClose}>
                <X class="w-5 h-5" />
              </button>
            </div>

            <div class="panel-body directory-browser-body">
              <Show when={rootPath()}>
                <div class={`directory-browser-current ${props.mode === "files" ? "directory-browser-current--files" : ""}`}>
                  <span class="directory-browser-current-label">{t("directoryBrowser.currentFolder")}</span>
                  <DirectoryBrowserAddress
                    value={pathInput()}
                    destinations={destinations()}
                    open={addressOpen() && !creatingFolder()}
                    onOpenChange={setAddressOpen}
                    onInput={(value) => { setPathInput(value); setPathInputDirty(true) }}
                    onReset={() => { setPathInputDirty(false); setPathInput(currentAbsolutePath()) }}
                    onSubmit={() => void handlePathSubmit()}
                    onChoose={chooseDestination}
                    canGo={canGoToTypedPath() && !creatingFolder()}
                  />
                  <Show when={props.mode !== "files"}>
                    <button
                      type="button"
                      class="selector-button selector-button-secondary directory-browser-select directory-browser-new-folder"
                      disabled={!canSelectCurrent() || creatingFolder()}
                      onClick={() => void handleCreateFolder()}
                    >
                      <span class="inline-flex items-center gap-2">
                        <FolderPlus class="w-4 h-4" />
                        {creatingFolder() ? t("directoryBrowser.creating") : t("directoryBrowser.newFolder")}
                      </span>
                    </button>
                  </Show>
                  <button
                    type="button"
                    class="selector-button selector-button-secondary directory-browser-open-path"
                    disabled={(!canSelectCurrent() && !canSubmitPath()) || creatingFolder()}
                    onClick={() => void handleOpenCurrent()}
                    title={t("directoryBrowser.openCurrent")}
                    aria-label={t("directoryBrowser.openCurrent")}
                  >
                    <ArrowRightSquare class="w-4 h-4" />
                    <span>{t("directoryBrowser.openCurrent")}</span>
                  </button>
                </div>
              </Show>
              <Show
                when={!loading() && !error()}
                fallback={
                  <div class="panel-empty-state flex-1">
                    <Show when={loading()} fallback={<span class="text-red-500">{error()}</span>}>
                      <div class="directory-browser-loading">
                        <Loader2 class="w-5 h-5 animate-spin" />
                        <span>{t("directoryBrowser.loadingFolders")}</span>
                      </div>
                    </Show>
                  </div>
                }
              >
                <Show
                  when={folderRows().length > 0 || Boolean(currentMetadata()?.parentPath)}
                  fallback={<div class="panel-empty-state flex-1">{t("directoryBrowser.noFolders")}</div>}
                >
                  <div class="panel-list panel-list--fill flex-1 min-h-0 overflow-auto directory-browser-list" role="listbox">
                    <Show when={currentMetadata()?.parentPath}>
                      {(parent) => (
                        <div class="panel-list-item" role="option">
                          <div class="panel-list-item-content directory-browser-row">
                            {/* Keep address blur from reflowing the list before the click lands. */}
                            <button type="button" class="directory-browser-row-main" disabled={creatingFolder()}
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={() => handleNavigateTo(parent())}>
                              <div class="directory-browser-row-icon"><ArrowUpLeft class="w-4 h-4" aria-hidden="true" /></div>
                              <div class="directory-browser-row-text">
                                <span class="directory-browser-row-name">{t("directoryBrowser.upOneLevel")}</span>
                              </div>
                            </button>
                          </div>
                        </div>
                      )}
                    </Show>
                    <For each={folderRows()}>
                      {(entry) => {
                        const isFolder = entry.type === "directory"
                        const isSelectable = props.mode === "files" ? entry.type === "file" : isFolder
                        const navigate = () => {
                          if (isFolder) {
                            handleNavigateTo(entry.path)
                            return
                          }
                          handleEntrySelect(entry)
                        }
                        return (
                          <div class="panel-list-item" role="option">
                            <div class="panel-list-item-content directory-browser-row">
                              <button type="button" class="directory-browser-row-main" onClick={navigate}>
                                <div class="directory-browser-row-icon">
                                  <Show when={!isFolder} fallback={<FolderIcon class="w-4 h-4" />}>
                                    <FileIcon class="w-4 h-4" />
                                  </Show>
                                </div>
                                <div class="directory-browser-row-text">
                                  <span class="directory-browser-row-name">{entry.name || entry.path}</span>
                                </div>
                                <Show when={isFolder && isPathLoading(entry.path)}>
                                  <Loader2 class="directory-browser-row-spinner animate-spin" />
                                </Show>
                              </button>
                              {isSelectable ? (
                                <button
                                  type="button"
                                  class="selector-button selector-button-secondary directory-browser-select"
                                  onClick={(event) => {
                                    event.stopPropagation()
                                     handleEntrySelect(entry)
                                  }}
                                >
                                  {t("directoryBrowser.select")}
                                </button>
                              ) : null}
                            </div>
                          </div>
                        )
                      }}
                    </For>
                  </div>
                </Show>
              </Show>
            </div>
          </div>
        </div>
      </div>
    </Show>
  )
}

export default DirectoryBrowserDialog
